import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, Apple, Brain, Moon, Dumbbell, Shield, ChevronRight,
  Search, ArrowLeft, BookOpen, Lightbulb
} from "lucide-react";

const CATEGORIES = [
  { key: "all",       label: "All",           icon: Lightbulb,  color: "bg-slate-100 text-slate-600" },
  { key: "nutrition", label: "Nutrition",      icon: Apple,      color: "bg-green-100 text-green-600" },
  { key: "exercise",  label: "Exercise",       icon: Dumbbell,   color: "bg-blue-100 text-blue-600"   },
  { key: "mental",    label: "Mental Health",  icon: Brain,      color: "bg-purple-100 text-purple-600"},
  { key: "sleep",     label: "Sleep",          icon: Moon,       color: "bg-indigo-100 text-indigo-600"},
  { key: "preventive",label: "Preventive",     icon: Shield,     color: "bg-rose-100 text-rose-600"   },
];

const TIPS = [
  {
    id: 1, category: "nutrition",
    title: "Eat a Rainbow",
    body: "Include colorful fruits and vegetables in every meal. Different colors provide different vitamins, minerals, and antioxidants your body needs.",
    icon: Apple, badge: "Essential", badgeColor: "bg-green-100 text-green-700",
  },
  {
    id: 2, category: "nutrition",
    title: "Stay Hydrated",
    body: "Drink 8–10 glasses of water daily. Hydration improves concentration, digestion, and skin health. Start your morning with a full glass of water.",
    icon: Heart, badge: "Daily Habit", badgeColor: "bg-blue-100 text-blue-700",
  },
  {
    id: 3, category: "exercise",
    title: "30 Minutes of Movement",
    body: "Even a brisk 30-minute walk daily significantly reduces risk of heart disease, type 2 diabetes, and depression. Every step counts!",
    icon: Dumbbell, badge: "Recommended", badgeColor: "bg-blue-100 text-blue-700",
  },
  {
    id: 4, category: "exercise",
    title: "Strength Training",
    body: "Do resistance exercises 2–3 times per week. It builds muscle mass, strengthens bones, and improves metabolism as you age.",
    icon: Dumbbell, badge: "Weekly", badgeColor: "bg-blue-100 text-blue-700",
  },
  {
    id: 5, category: "mental",
    title: "Practice Mindfulness",
    body: "Spend 10 minutes daily on mindful breathing or meditation. It reduces stress hormones, improves focus, and lowers blood pressure.",
    icon: Brain, badge: "Daily", badgeColor: "bg-purple-100 text-purple-700",
  },
  {
    id: 6, category: "mental",
    title: "Digital Detox",
    body: "Take a 1-hour break from screens before bedtime. Blue light disrupts melatonin production and delays sleep onset.",
    icon: Brain, badge: "Night Routine", badgeColor: "bg-purple-100 text-purple-700",
  },
  {
    id: 7, category: "sleep",
    title: "Consistent Sleep Schedule",
    body: "Sleep and wake at the same time every day — even weekends. A regular rhythm improves sleep quality and daytime energy.",
    icon: Moon, badge: "Vital", badgeColor: "bg-indigo-100 text-indigo-700",
  },
  {
    id: 8, category: "sleep",
    title: "Optimal Sleep Duration",
    body: "Adults need 7–9 hours of quality sleep. Chronic sleep deprivation is linked to obesity, heart disease, and weakened immunity.",
    icon: Moon, badge: "Science-backed", badgeColor: "bg-indigo-100 text-indigo-700",
  },
  {
    id: 9, category: "preventive",
    title: "Regular Health Screenings",
    body: "Annual checkups can catch conditions like hypertension, diabetes, and cancer early — when they are much easier to treat.",
    icon: Shield, badge: "Annual", badgeColor: "bg-rose-100 text-rose-700",
  },
  {
    id: 10, category: "preventive",
    title: "Vaccinations Stay Current",
    body: "Keep your vaccinations up to date. Flu shots, tetanus boosters, and COVID vaccines protect you and those around you.",
    icon: Shield, badge: "Preventive", badgeColor: "bg-rose-100 text-rose-700",
  },
  {
    id: 11, category: "nutrition",
    title: "Reduce Ultra-Processed Foods",
    body: "Limit chips, packaged snacks, and sugary drinks. They are linked to obesity, inflammation, and cardiovascular disease.",
    icon: Apple, badge: "Important", badgeColor: "bg-orange-100 text-orange-700",
  },
  {
    id: 12, category: "mental",
    title: "Social Connections Matter",
    body: "Strong social relationships are one of the best predictors of long-term health. Make time for friends and family regularly.",
    icon: Heart, badge: "Well-being", badgeColor: "bg-pink-100 text-pink-700",
  },
];

const HealthTips = ({ user }) => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = TIPS.filter(
    (t) =>
      (activeCategory === "all" || t.category === activeCategory) &&
      (t.title.toLowerCase().includes(search.toLowerCase()) ||
        t.body.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-green-50 via-white to-blue-50">
      {/* Header */}
      <div className="glass-panel sticky top-0 z-40 px-4 py-4 border-b border-white/30">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-semibold text-slate-800">Health Tips</h1>
              <p className="text-xs text-slate-500">Preventive care &amp; wellness advice</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tips…"
            className="w-full pl-10 pr-4 py-3 rounded-2xl bg-white/80 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-300 placeholder-slate-400"
          />
        </motion.div>

        {/* Category pills */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                activeCategory === cat.key
                  ? "bg-green-500 text-white shadow-sm"
                  : "bg-white/80 text-slate-600 border border-slate-200"
              }`}
            >
              <cat.icon className="w-3.5 h-3.5" />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Featured banner */}
        {activeCategory === "all" && !search && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl p-5 text-white"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg">Today's Tip</h2>
                <p className="text-green-100 text-sm mt-1 leading-relaxed">
                  Drink a glass of water first thing in the morning. It kick-starts your metabolism,
                  flushes toxins, and improves brain function throughout the day.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Tips grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No tips found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((tip, i) => {
              const cat = CATEGORIES.find((c) => c.key === tip.category);
              return (
                <motion.div
                  key={tip.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="glass-card rounded-2xl p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${cat?.color}`}>
                      <tip.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-semibold text-slate-800 text-sm">{tip.title}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${tip.badgeColor}`}>
                          {tip.badge}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">{tip.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthTips;
